/**
 * Data hooks of the can-do path (plan 46, P5). Each one resolves to an
 * explicit state, and `off` exists only for the API's flag-off answer
 * (`isCanDoPathOff`): a network or server failure is `error`, never "off" —
 * the old Program must not appear because a request failed.
 *
 * `useCanDoPath` / `useCanDoNext` keep a session cache (stale-while-revalidate,
 * like `useProgram`): a revisit mounts with the last answer and revalidates.
 * The flag the session already knows (`knownCanDoFlag`) seeds the first
 * render, so a learner without the flag sees today's screens without a
 * loader in between once any can-do answer came back.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  fetchCanDoNext,
  fetchCanDoPath,
  fetchCanDoUnit,
  isCanDoPathOff,
  knownCanDoFlag,
  type CanDoLevel,
  type CanDoNextStepView,
  type CanDoPathView,
  type CanDoUnitDetail,
} from '../../../lib/canDoApi.js';
import { createSessionCache } from '../../../lib/sessionCache.js';

export type CanDoLoad<T> =
  | { status: 'loading' }
  | { status: 'off' }
  | { status: 'on'; data: T }
  | { status: 'error'; message: string };

export type CanDoLoadState<T> = CanDoLoad<T> & { reload: () => void };

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function initial<T>(cached: T | null): CanDoLoad<T> {
  if (cached) return { status: 'on', data: cached };
  return knownCanDoFlag() === 'off' ? { status: 'off' } : { status: 'loading' };
}

/**
 * One cached can-do read: seeds from the cache / known flag, always
 * revalidates on mount and on `reload`.
 */
function useCachedCanDo<T>(
  cache: { get: () => T | null; set: (v: T | null) => void },
  load: (signal: AbortSignal) => Promise<T>,
): CanDoLoadState<T> {
  const [state, setState] = useState<CanDoLoad<T>>(() => initial(cache.get()));
  const [round, setRound] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal)
      .then((data) => {
        cache.set(data);
        if (!ac.signal.aborted) setState({ status: 'on', data });
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        if (isCanDoPathOff(err)) {
          cache.set(null);
          setState({ status: 'off' });
          return;
        }
        setState({ status: 'error', message: errorMessage(err) });
      });
    return () => ac.abort();
    // `cache` and `load` are module-level (stable); `round` re-runs on reload.
  }, [cache, load, round]);

  const reload = useCallback(() => {
    setState((s) => (s.status === 'error' ? { status: 'loading' } : s));
    setRound((r) => r + 1);
  }, []);

  return { ...state, reload };
}

const pathCache = createSessionCache<CanDoPathView>();
const nextCache = createSessionCache<{ learnerLevel: CanDoLevel; next: CanDoNextStepView }>();

/** `GET /can-do`: the whole path (Program screen, grammar library). */
export function useCanDoPath(): CanDoLoadState<CanDoPathView> {
  return useCachedCanDo(pathCache, fetchCanDoPath);
}

/** `GET /can-do/next`: just the next step (home card). */
export function useCanDoNext(): CanDoLoadState<{
  learnerLevel: CanDoLevel;
  next: CanDoNextStepView;
}> {
  return useCachedCanDo(nextCache, fetchCanDoNext);
}

export type CanDoUnitState =
  | { status: 'loading' }
  | { status: 'ready'; detail: CanDoUnitDetail }
  | { status: 'error'; message: string };

/** `GET /can-do/units/:unitId` for the open panel (`null` = nothing open). */
export function useCanDoUnit(unitId: string | null): CanDoUnitState & { reload: () => void } {
  const [state, setState] = useState<CanDoUnitState>({ status: 'loading' });
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!unitId) return undefined;
    const ac = new AbortController();
    setState({ status: 'loading' });
    fetchCanDoUnit(unitId, ac.signal)
      .then((detail) => {
        if (!ac.signal.aborted) setState({ status: 'ready', detail });
      })
      .catch((err: unknown) => {
        if (!ac.signal.aborted) setState({ status: 'error', message: errorMessage(err) });
      });
    return () => ac.abort();
  }, [unitId, round]);

  const reload = useCallback(() => setRound((r) => r + 1), []);
  return { ...state, reload };
}
